import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { createTheme, ThemeProvider } from '@mui/material';
import { SIGNIN } from './redux/actionTypes/authActionType';
import { PageNotFound } from './Components/Errors/PageNotFound';
import { routesArray } from './routes';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


// organizer Routes
import GradeOrg from './Organizer/CreateGradeEvent/GradeOrg';
import FiesOrg from './Organizer/CreateFeisEvent/FiesOrg';
import OireachtasOrg from './Organizer/CreateOireachtasEvent/OireachtasOrg';
import LoginOrg from './Organizer/Auth/LoginOrg';
import PairScan, { PairSignIn } from './device/PairScan';
import ForgotPasswordOrg from './Organizer/Auth/ForgotPasswordOrg';
import ConfirmPasswordOrg from './Organizer/Auth/ConfirmPasswordOrg';
import DashboardOrg from './Organizer/Dashboard/DashboardOrg';
import ManageFeisGrade from './Organizer/ManageFeisGrade/ManageFeisGrade';
import ManageOireachtas from './Organizer/ManageOireachtas/ManageOireachtas';
import OireachtasDraw from './Organizer/ManageOireachtas/OireachtasDraw';
import OireachtasDrawRoom from './Organizer/ManageOireachtas/Draw/OireachtasDrawRoom';
import OireachtasCheckIn from './Organizer/ManageOireachtas/OireachtasCheckIn';
import OireachtasTabulation from './Organizer/ManageOireachtas/OireachtasTabulation';
import OireachtasResults from './Organizer/ManageOireachtas/OireachtasResults';
import OireachtasStatus from './Organizer/ManageOireachtas/OireachtasStatus';
import OireachtasCompetitors from './Organizer/ManageOireachtas/OireachtasCompetitors';
import OireachtasPrint from './Organizer/ManageOireachtas/OireachtasPrint';
import OireachtasStripe from './Organizer/ManageOireachtas/OireachtasStripe';
import OireachtasReporting from './Organizer/ManageOireachtas/OireachtasReporting';
import OrganizerOireachtas from './Organizer/OireachtasPanel/OrganizerOireachtas';
import OireachtasSchedule from './Organizer/OireachtasPanel/Schedule/OireachtasSchedule';
import DefaultSyllabi from './Organizer/DefaultSyllabi/DefaultSyllabi';
import PaymentRequests from './Organizer/PaymentRequests/PaymentRequests';
import EventDetails from './Organizer/ManageFeisGrade/RedirectFeis/Event/EventDetails';
import StripeAccount from './Organizer/Payment/StripeAccount';
import FeisCompetitors from './Organizer/ManageFeisGrade/RedirectFeis/Entries/FeisCompetitors';
import EditCompetitors from './Organizer/ManageFeisGrade/RedirectFeis/Entries/EditCompetitors';
import EditRegister from './Organizer/ManageFeisGrade/RedirectFeis/Entries/EditRegister';
import MergeSplit from './Organizer/ManageFeisGrade/RedirectFeis/Entries/MergeSplit';
import MergeSplitFn from './Organizer/ManageFeisGrade/RedirectFeis/Entries/MergeSplitFn';
import Email from './Organizer/ManageFeisGrade/RedirectFeis/Entries/Email';
import FinalFiezDetails from './Organizer/GoLive/FinalFiezDetails';
import FinalOireachtasDetails from './Organizer/GoLive/FinalOireachtasDetails';
import CheckIn from './Organizer/ManageFeisGrade/RedirectFeis/Tabulation/CheckIn';
import ResultStatus from './Organizer/ManageFeisGrade/RedirectFeis/Tabulation/ResultStatus';
import Tabulate from './Organizer/ManageFeisGrade/RedirectFeis/Tabulation/Tabulate';
import CompleteResults from './Organizer/ManageFeisGrade/RedirectFeis/Tabulation/CompleteResults';
import ScoreCheckInDownload from './Organizer/ManageFeisGrade/RedirectFeis/Print/ScoreCheckInDownload';
import NowNextSigns from './Organizer/ManageFeisGrade/RedirectFeis/Print/NowNextSigns';
import Transactions from './Organizer/ManageFeisGrade/RedirectFeis/Financial/Transactions';
import CandidateLists from './Organizer/ManageFeisGrade/RedirectGrade/Entries/CandidateLists';
import PublishedResults from './Organizer/ManageFeisGrade/RedirectFeis/Tabulation/PublishedResults';
import ManageUsers from './Organizer/Dashboard/ManageUsers';
import AwardsTableComponent from './Organizer/AwardsTable/AwardsTableComponent';
import ManageSchools from './Organizer/Dashboard/ManageSchools';
import EditCandidates from './Organizer/ManageFeisGrade/RedirectGrade/Entries/EditCandidates';
import GradeEditRegister from './Organizer/ManageFeisGrade/RedirectGrade/Entries/GradeEditRegister';
import Dashboard from './Organizer/Dashboard/Dashboard';
import CreateEventFromPrev from './Organizer/CreateEventFromPrev/CreateEventFromPrev';
import ManageSchedule from './Organizer/ManageFeisGrade/RedirectFeis/Schedule/ManageSchedule';
import ManageCollaborators from './Organizer/Dashboard/ManageCollaborators';
import SchoolDetails from './Organizer/Dashboard/SchoolDetails';
import ManageDancers from './Organizer/Dashboard/ManageDancers';
import DancerDetails from './Organizer/Dashboard/DancerDetails';
import ParentDetails from './Organizer/Dashboard/ParentDetails';
// Event-day job devices (card 540f21a4)
import JobRoles from './Organizer/JobRoles/JobRoles';
import JobDevices from './Organizer/JobDevices/JobDevices';
import { DeviceProvider } from './device/DeviceContext';
import DeviceHome from './device/DeviceHome';
import DeviceEnded from './device/DeviceEnded';
import RequireFunction from './device/RequireFunction';
import { OX_ROUTES } from './device/deviceScreens';
import { forgetDevice, readDevice } from './device/deviceSession';
import { deviceErrorAction, isDeviceRequest } from './device/deviceErrors';
import { useSingleResponseInterceptor } from './helper/singleInterceptor';

// The admin screens an Oireachtas job device can open (device/deviceScreens.js).
const DEVICE_SCREEN_ELEMENTS = {
  checkin: OireachtasCheckIn,
  tabulation: OireachtasTabulation,
  status: OireachtasStatus,
  results: OireachtasResults,
  draw: OireachtasDraw,
  competitors: OireachtasCompetitors,
  print: OireachtasPrint,
};


const theme = createTheme({
  palette: {
    primary: {
      main: '#68AD3F',
    },
    secondary: {
      main: '#FBB040',
    },
  },

  typography: {
    fontFamily: '"Rubik", sans-serif ',
    h1: {
      fontSize: '52px',
      fontWeight: 700,
    },
    h2: {
      fontSize: '40px',
      fontWeight: 500,
    },
    button: {
      textTransform: 'none',
    },
  },
});
const NotFoundRedirect = () => {
  if (routesArray.includes(window.location.pathname)) {
    const fullPath = window.location.pathname + window.location.search;
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.search);
    const type = params.get('type');
    if (type === "redirect") {
      localStorage.setItem("orgPath", fullPath)
      localStorage.setItem("redirectFromOrg", "redirect")
    }
    return <Navigate to="/" state={{ from: '404PAGE' }} />;
  } else {
    return <PageNotFound />;
  }
};
console.log("app")

export const AppWrapper = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isToastVisible, setIsToastVisible] = useState(true);
  const accessToken = localStorage.getItem('accessToken');
  // a paired Oireachtas job device runs its own small set of screens
  const jobDevice = readDevice();
  const oxDevice = jobDevice && jobDevice.eventKind === 'oireachtas' ? jobDevice : null;
  //Authenticating user while the session is live or user is logged in
  const authenticateUser = () => {
    if (accessToken) {
      dispatch({
        type: SIGNIN,
        payload: {
          accessToken,
        },
      });
    }
  };
  useEffect(() => {
    authenticateUser();
  }, []);

  // Add a request interceptor
  axios.interceptors.request.use(
    (config) => {
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  // One response interceptor for the app's lifetime: it used to be added
  // again on every render, so one failed request ran the handler many times
  // (and an ended device's later copies fell into the sign-out branch —
  // MC review 56b15e4d R1-F1). As on the website (helper/singleInterceptor.js).
  useSingleResponseInterceptor(axios, handleResponse, handleError);

  function handleResponse(response) {
    return response;
  }

  function handleError(error) {
    // A paired event-day device never signs out or clears the browser: its
    // access ended, or its job doesn't include that (card 540f21a4). Decided
    // by the request too: requests still out when the device ended carry its
    // token and must not reach the sign-out handling below.
    if (isDeviceRequest(error) || readDevice()) {
      const action = deviceErrorAction(error);
      if (action === 'ended') {
        if (readDevice()) {
          forgetDevice();
          navigate('/device-ended', { replace: true });
        }
      } else if (action === 'notGranted') {
        // (a fixed id: this handler can be registered more than once)
        toast.error("This device's job doesn't include that.", { position: toast.POSITION.TOP_RIGHT, toastId: 'device-not-granted' });
      } else if (action === 'unavailable') {
        toast.error("That isn't available on an event device.", { position: toast.POSITION.TOP_RIGHT, toastId: 'device-unavailable' });
      }
      return Promise.reject(error);
    }
    if (!error.response) return Promise.reject(error);
    if (error.response.status === 401) {
      localStorage.clear();
      navigate('/', {
        state: { from: `${error.response.data.message.name}` },
      });
      return Promise.reject(error);
    }

    if (error.response.status === 500) {
      if (isToastVisible) {
        toast.dismiss();
        toast.error('Some Thing went wrong', {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 100,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          progress: undefined,
        });
        setIsToastVisible(false);
      }
    }


    if (error.response.status === 403) {
      localStorage.clear();
      navigate('/', {
        state: { from: `${error.response.data.message}` },
      });
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }

  return (
    <div className="App">
      <ThemeProvider theme={theme}>
        <DeviceProvider onEnded={() => navigate('/device-ended', { replace: true })}>
        {oxDevice ? (
          <Routes>
            <Route exact path="/" element={<DeviceHome />} />
            {OX_ROUTES.map((r) => {
              const Screen = DEVICE_SCREEN_ELEMENTS[r.key];
              return (
                <Route
                  key={r.path}
                  path={r.path}
                  element={
                    <RequireFunction any={r.any}>
                      <Screen />
                    </RequireFunction>
                  }
                />
              );
            })}
            <Route path="/device-ended" element={<DeviceEnded />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        ) : accessToken ? (
          <Routes>
            <Route exact path="/" element={<Dashboard />} />
            <Route path="/feis/manage" element={<ManageFeisGrade />} />
            <Route path="/grade/manage" element={<ManageFeisGrade />} />
            <Route path="/event-details" exact element={<EventDetails />} />
            <Route path="/users" exact element={<ManageUsers />} />
            <Route path="/schools" exact element={<ManageSchools />} />
            <Route path="/schools/details/:id" exact element={<SchoolDetails />} />
            <Route path="/collaborators" exact element={<ManageCollaborators />} />
            <Route path="/dancers" exact element={<ManageDancers />} />
            <Route path="/dancers/details/:id" exact element={<DancerDetails />} />
            <Route path="/parents/details/:id" exact element={<ParentDetails />} />
            <Route path="/feis" exact element={<DashboardOrg />} />
            <Route path="/grade" exact element={<DashboardOrg />} />
            <Route path="/oireachtas" exact element={<DashboardOrg />} />
            <Route path="/oireachtas/manage" exact element={<ManageOireachtas />} />
            <Route path="/oireachtas/draw" exact element={<OireachtasDraw />} />
            <Route path="/oireachtas/draws" exact element={<OireachtasDrawRoom />} />
            <Route path="/oireachtas/check-in" exact element={<OireachtasCheckIn />} />
            <Route path="/oireachtas/tabulation" exact element={<OireachtasTabulation />} />
            <Route path="/oireachtas/results" exact element={<OireachtasResults />} />
            <Route path="/oireachtas/status" exact element={<OireachtasStatus />} />
            <Route path="/oireachtas/competitors" exact element={<OireachtasCompetitors />} />
            <Route path="/oireachtas/print/:artifact" element={<OireachtasPrint />} />
            <Route path="/oireachtas/stripe" exact element={<OireachtasStripe />} />
            <Route path="/oireachtas/reporting" exact element={<OireachtasReporting />} />
            <Route path="/oireachtas/organizer-view" exact element={<OrganizerOireachtas />} />
            <Route path="/oireachtas/schedule" exact element={<OireachtasSchedule />} />
            <Route path="/default-syllabi" exact element={<DefaultSyllabi />} />
            <Route path="/payment-requests" exact element={<PaymentRequests />} />
            <Route path="/feis/create" exact element={<FiesOrg />} />
            <Route path="/oireachtas/create" exact element={<OireachtasOrg />} />
            <Route path="/grade/create" exact element={<GradeOrg />} />
            <Route path="/previous-events" element={<CreateEventFromPrev />} />


            <Route path="/grade/create/finalGradeDetails" element={<FinalFiezDetails type="grade" />} />
            <Route path="/feis/create/finalFeisDetails" element={<FinalFiezDetails type="feis" />} />
            <Route path="/oireachtas/create/finalOireachtasDetails" element={<FinalOireachtasDetails />} />
            <Route path="/competitorss/:name" element={<FeisCompetitors />} />
            <Route path="/candidate/:name" element={<CandidateLists />} />
            <Route path="/edit-candidates/:name" element={<EditCandidates />} />
            <Route path="/grade/edit-register/:name" exact element={<GradeEditRegister />} />
            <Route path="/edit-competitors/:name" element={<EditCompetitors />} />
            <Route path="/stripe-account" exact element={<StripeAccount />} />
            {/* Stripe returns admins here after "Stripe account" linking (the
                redirect registered with Stripe); it is the same page. */}
            <Route path="/organizer-account" exact element={<StripeAccount />} />
            <Route path="/edit-register/:name" exact element={<EditRegister />} />
            <Route path="/merge-split/:name" exact element={<MergeSplit />} />
            <Route path="/merge-split-comp/:name" exact element={<MergeSplitFn />} />
            <Route path="/email" exact element={<Email />} />
            <Route path="/check-in/:name" exact element={<CheckIn />} />
            <Route path="/result-status/:name" exact element={<ResultStatus />} />
            <Route path="/tabulate/:name" exact element={<Tabulate />} />
            <Route path="/complete-results/:name" exact element={<CompleteResults />} />
            <Route path="/score-checkin-download/:name" exact element={<ScoreCheckInDownload />} />
            <Route path="/now-next-signs/:name" exact element={<NowNextSigns />} />
            <Route path="/transaction-history/:name" exact element={<Transactions />} />
            <Route path="/published-results/:name" exact element={<PublishedResults />} />
            <Route path="/awards" exact element={<AwardsTableComponent />} />
            <Route path="/manage-schedule" exact element={<ManageSchedule />} />
            <Route path="/job-roles" exact element={<JobRoles />} />
            <Route path="/job-devices" exact element={<JobDevices />} />
            {/* a phone that scanned a device's QR code (card 540f21a4) */}
            <Route path="/pair" element={<PairScan />} />
            <Route path="/device-ended" element={<DeviceEnded />} />
            <Route path="*" exact element={<PageNotFound />} />

          </Routes>
        ) : (
          <Routes>
            <Route path="/" element={<LoginOrg />} />
            <Route path="/admin/login" element={<LoginOrg />} />
            <Route path="/pair" element={<PairSignIn />} />
            <Route path="/forgotpwd" element={<ForgotPasswordOrg />} />
            <Route path="/reset-pwd" element={<ConfirmPasswordOrg />} />
            <Route path="/device-ended" element={<DeviceEnded />} />
            <Route path="*" element={<NotFoundRedirect />} />
          </Routes>
        )}
        </DeviceProvider>
        <ToastContainer />
      </ThemeProvider>
    </div>
  );
};
